import React from "react";
import { Card, Empty, ErrorState, PageHeading, Pill, RowsSkeleton, Table } from "../ui";
import { Meter } from "../charts";
import { useFilters } from "../filters";
import { useView } from "../data";
import { dayShort, fmt, ms, pct, signed, usd, whenShort } from "../format";

/**
 * Each figure the release table compares, how to print it and its change, and
 * which way is better (`up` / `down`; null = neither).
 */
const FIGURES = [
  { key: "taskOutcome", label: "Task outcome", show: (v) => (v == null ? "—" : v.toFixed(2)), delta: (d) => signed(d.toFixed(2)), better: "up" },
  { key: "successRate", label: "Success", show: pct, delta: (d) => `${signed(Math.round(d * 100))} pts`, better: "up" },
  { key: "stepCompletion", label: "Steps finished", show: pct, delta: (d) => `${signed(Math.round(d * 100))} pts`, better: "up" },
  { key: "criteriaFidelity", label: "Criteria kept", show: pct, delta: (d) => `${signed(Math.round(d * 100))} pts`, better: "up" },
  { key: "flowHealth", label: "Flow health", show: (v) => (v == null ? "—" : v.toFixed(2)), delta: (d) => signed(d.toFixed(2)), better: "up" },
  { key: "frictionPerTurn", label: "Stuck / turn", show: (v) => fmt(v, 2), delta: (d) => signed(d.toFixed(2)), better: "down" },
  { key: "problemsPerTurn", label: "Problems / turn", show: (v) => fmt(v, 2), delta: (d) => signed(d.toFixed(2)), better: "down" },
  { key: "costPerTurn", label: "Cost / turn", show: usd, delta: (d) => `${d < 0 ? "−" : "+"}${usd(Math.abs(d))}`, better: "down" },
  { key: "avgAgentMs", label: "Agent time", show: ms, delta: (d) => `${d < 0 ? "−" : "+"}${ms(Math.abs(Math.round(d)))}`, better: "down" },
];

/** Each deployed release against the one before it, and the golden sets recorded before each deploy. */
export default function Versions() {
  const { params } = useFilters();
  const { data, loading, error } = useView("releases", params);
  const releases = data?.releases ?? [];
  const golden = data?.golden ?? [];
  const log = data?.releaseLog ?? {};

  const cell = (fig) => (r) => {
    const d = r.change?.[fig.key];
    const moved = typeof d === "number" && Math.abs(d) > 1e-9;
    const good = fig.better && moved && (fig.better === "up" ? d > 0 : d < 0);
    return (
      <span>
        <span>{fig.show(r[fig.key])}</span>
        {moved && (
          <span className={`aa-delta ${!fig.better ? "aa-delta-flat" : good ? "aa-delta-good" : "aa-delta-bad"}`} style={{ display: "table", marginLeft: "auto", marginTop: 3, fontSize: 11 }}>
            {fig.delta(d)}
          </span>
        )}
      </span>
    );
  };

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PageHeading title="Releases">Each deployed version against the one before it, and the test suites recorded before each deploy — so a change that made things worse is caught.</PageHeading>

      <Card title="Release against release" subtitle="Newest first · change against the release before · a release with few turns is noise, read the turn count first" flush>
        {loading ? (
          <RowsSkeleton />
        ) : error ? (
          <ErrorState error={error} />
        ) : releases.length ? (
          <Table
            columns={[
              {
                key: "release",
                label: "Release",
                sortable: false,
                render: (r) => (
                  <span>
                    <span className="aa-mono" style={{ fontWeight: 600, fontSize: 13 }}>
                      {r.release}
                    </span>
                    <span className="aa-sub">
                      {log[r.release]?.title ? `${log[r.release].title} · ` : ""}
                      {dayShort(r.firstSeen)} – {dayShort(r.lastSeen)}
                    </span>
                  </span>
                ),
              },
              { key: "turns", label: "Turns", align: "right", sortable: false, render: (r) => <span title={`${fmt(r.judgedTurns)} judged`}>{fmt(r.turns)}</span> },
              ...FIGURES.map((fig) => ({ key: fig.key, label: fig.label, align: "right", sortable: false, render: cell(fig) })),
              { key: "recordsSaved", label: "Records saved", align: "right", sortable: false, render: (r) => fmt(r.recordsSaved) },
            ]}
            rows={releases.map((r) => ({ ...r, id: r.release }))}
          />
        ) : (
          <Empty icon="versions" title="No release recorded signals in this period" />
        )}
      </Card>

      <Card title="What shipped" subtitle="Each release's manifest, approved at deploy · issues it claimed and where they stand">
        {loading ? (
          <RowsSkeleton />
        ) : releases.length ? (
          <div style={{ display: "grid", gap: 18 }}>
            {releases.map((r) => (
              <ReleaseManifest key={r.release} release={r.release} entry={log[r.release]} />
            ))}
          </div>
        ) : (
          <Empty icon="versions" title="No release in this period" />
        )}
      </Card>

      <Card title="Golden sets" subtitle="Recorded by the deploy gate (npm run eval:gate)">
        {golden.length ? (
          <div style={{ display: "grid", gap: 24 }}>
            {golden.map((g) => (
              <div key={g.datasetId}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{g.name}</span>
                  <Pill tone="neutral">{fmt(g.cases)} cases</Pill>
                </div>
                {g.runs.length ? (
                  <div className="aa-card" style={{ boxShadow: "none", overflow: "hidden" }}>
                    <Table
                      columns={[
                        { key: "release", label: "Release", render: (r) => <span className="aa-mono">{r.release ?? "—"}</span> },
                        { key: "startedAt", label: "When", nowrap: true, render: (r) => whenShort(r.startedAt) },
                        {
                          key: "passRate",
                          label: "Pass rate",
                          align: "right",
                          render: (r) => (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                              <Meter value={r.passRate} width={56} color={r.passRate >= 0.95 ? "var(--aa-good)" : r.passRate >= 0.8 ? "var(--aa-warn)" : "var(--aa-bad)"} />
                              <span style={{ minWidth: 40, fontWeight: 600 }}>{pct(r.passRate)}</span>
                            </span>
                          ),
                        },
                        { key: "passed", label: "Passed", align: "right", render: (r) => `${fmt(r.passed)} / ${fmt(r.total)}` },
                        { key: "regressed", label: "Regressed", align: "right", render: (r) => (r.regressed == null ? "—" : r.regressed ? <Pill tone="bad">{fmt(r.regressed)}</Pill> : <span style={{ color: "var(--aa-faint)" }}>0</span>) },
                        { key: "fixed", label: "Fixed", align: "right", render: (r) => (r.fixed == null ? "—" : r.fixed ? <Pill tone="good">{fmt(r.fixed)}</Pill> : <span style={{ color: "var(--aa-faint)" }}>0</span>) },
                        {
                          key: "cases",
                          label: "Cases that changed",
                          sortable: false,
                          max: 420,
                          render: (r) =>
                            r.regressedCases.length || r.fixedCases.length ? (
                              <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                {r.regressedCases.map((k) => (
                                  <Pill key={`r${k}`} tone="bad">✗ {k}</Pill>
                                ))}
                                {r.fixedCases.map((k) => (
                                  <Pill key={`f${k}`} tone="good">✓ {k}</Pill>
                                ))}
                              </span>
                            ) : (
                              <span style={{ color: "var(--aa-faint)" }}>—</span>
                            ),
                        },
                      ]}
                      rows={g.runs.map((r) => ({ ...r, id: r.experimentId }))}
                    />
                  </div>
                ) : (
                  <Empty compact title="No completed run recorded yet" />
                )}
              </div>
            ))}
          </div>
        ) : (
          <Empty icon="versions" title="No golden set recorded yet">
            Run <code>npm run eval:gate</code> in hp-mcp-server (production deploys run it).
          </Empty>
        )}
      </Card>
    </div>
  );
}

const RELEASE_TONE = { deployed: "good", pending: "warn", failed: "bad" };
const ISSUE_TONE = { open: "warn", fixed: "neutral", verified: "good", not_improved: "bad", wont_fix: "neutral" };
const CHANGE_TONE = { fix: "good", feature: "neutral", change: "warn", chore: "neutral" };

/** One release's manifest. A release with no log row deployed without a release step — it is said, not hidden. */
function ReleaseManifest({ release, entry }) {
  if (!entry?.manifest) {
    return (
      <div>
        <span className="aa-mono" style={{ fontWeight: 600 }}>{release}</span>
        <span style={{ color: "var(--aa-faint)", marginLeft: 8 }}>No manifest — deployed without a release record</span>
      </div>
    );
  }
  const m = entry.manifest;
  const deployed = entry.deploys.filter((d) => d.status === "deployed").length;
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span className="aa-mono" style={{ fontWeight: 600 }}>{release}</span>
        <Pill tone={RELEASE_TONE[entry.status] ?? "neutral"}>{entry.status}</Pill>
        <span style={{ fontWeight: 600 }}>{m.title}</span>
        <span className="aa-sub">
          {entry.createdBy ?? "unknown"} · {whenShort(entry.createdAt)} · {fmt(deployed)} deploy{deployed === 1 ? "" : "s"}
          {entry.deploys.some((d) => d.dirty) ? " · included uncommitted changes" : ""}
        </span>
      </div>
      {m.summary && <div style={{ color: "var(--aa-muted)" }}>{m.summary}</div>}
      {m.changes?.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
          {m.changes.map((c, i) => (
            <li key={i}>
              <Pill tone={CHANGE_TONE[c.type] ?? "neutral"}>{c.type}</Pill> <span className="aa-mono">{c.area}</span> — {c.description}
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        <span className="aa-sub">Fixes:</span>
        {entry.issues.length ? (
          entry.issues.map((i) => (
            <Pill key={i.id} tone={ISSUE_TONE[i.status] ?? "neutral"} title={i.signal ?? undefined}>
              {i.id} · {i.title} · {i.status.replace("_", " ")}
            </Pill>
          ))
        ) : (
          <span style={{ color: "var(--aa-faint)" }}>none claimed</span>
        )}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        <span className="aa-sub">Expect:</span>
        {m.expect?.length ? (
          m.expect.map((e, i) => (
            <Pill key={i} tone="neutral" title={e.why || undefined}>
              {e.signal} {e.direction === "up" ? "↑" : e.direction === "down" ? "↓" : "→"}
            </Pill>
          ))
        ) : (
          <span style={{ color: "var(--aa-faint)" }}>nothing stated — cannot be verified against a signal</span>
        )}
      </div>
      {m.risks?.length > 0 && <div className="aa-sub">Risks: {m.risks.join(" · ")}</div>}
    </div>
  );
}
